import type {
  AutomationStatsDto,
  DfyBuildPositionDto,
  DfyBuildRunDto,
  DfyEtaDto,
  JourneyMode,
  Locale,
  SectionKey
} from "../../shared/dto";
import type { ClientGender } from "../../shared/gender";
import type { TrainingRunDto } from "../../shared/training-run";
import type { RailDisplayStatus, RailStep } from "../rail-model";
import type { ProductionMaterial, ProductionWait } from "../../shared/dfy-prerequisites";

export const STAFF_DETAIL_POLL_MS = 10_000;
export const STAFF_WAITING_ALERT_DAYS = 2;

export type StaffRole = "partner" | "admin";
/**
 * F138a: the nine-tab `StaffTab` union is GONE, deliberately deleted rather
 * than left unread. Six of its tabs showed panels the step workspace already
 * draws, and the three that had no other home became the three flat sections
 * below the work (src/staff/ClientTools.tsx). A surviving type is an invitation
 * to bring the tabs back one panel at a time.
 */
export type ClientSectionKey = "overview" | "activity" | "settings";
export type StaffStepStatus = RailDisplayStatus | "blocked" | "done_ahead";
export type StaffRailStep = RailStep & {
  position?: number;
  staffStatus?: StaffStepStatus;
  /** Canonical truth fields (shared/staff-lifecycle.ts, fixr4). */
  owner?: "team" | "client" | "alex" | "none";
  visibleVersion?: number | null;
  aheadOfUnlock?: boolean;
  production?: ProductionWait;
};

export interface StaffMutationOptions {
  method: "POST" | "PUT" | "PATCH" | "DELETE";
  csrfToken: string;
  body?: unknown;
  commandId?: string;
  fetcher?: typeof fetch;
}

export type StaffRun = (
  path: string,
  options: Omit<StaffMutationOptions, "csrfToken">,
  behavior?: { refresh?: boolean }
) => Promise<Response>;

export interface StaffSection {
  key: SectionKey;
  state: "locked" | "next" | "working" | "done";
  primaryNext: boolean;
  rowVersion: number;
  changedAt: string;
}

export interface StaffMatrixClient {
  id: string;
  name: string;
  email: string;
  /** When the RECORD was created; tells two records of one person apart. */
  createdAt?: string;
  is_test?: boolean;
  photoUrl: string | null;
  locale: Locale;
  axes: {
    includesApp: boolean;
    purchaseSource: string;
    instanceStartMode: string;
    /**
     * F143: which journey the client walks. Required, so the whole-client
     * editor cannot forget to send it back and blindly overwrite the column
     * (the S3-01 law).
     */
    journeyMode: JourneyMode;
    /** F64: served by the client DETAIL only; the matrix has no use for it. */
    gender?: ClientGender;
  };
  partner: { id: string; name: string | null } | null;
  currentPhase: SectionKey;
  phaseAgeDays: number;
  pendingFeedback: number;
  awaitingRelease?: boolean;
  pendingFeedbackAgeDays?: number;
  /** Canonical client owner + open team work (shared/staff-lifecycle.ts). */
  owner?: "partner" | "client" | "alex";
  teamOpenCount?: number;
  nextCall: null | {
    id: string;
    kind: string;
    locale: Locale;
    startsAt: string;
    zoomUrl: string;
    recordingUrl: string | null;
    enrollmentStatus: string | null;
  };
  sections: StaffSection[];
  steps: StaffRailStep[];
}

export type StaffQueueItemType =
  | "review_publish"
  | "client_blocked"
  | "step_blocked"
  | "service_action"
  | "automation_watch";

export interface StaffQueueItem {
  id: string;
  type: StaffQueueItemType;
  clientId: string;
  clientName: string;
  clientEmail?: string;
  /** When the client RECORD was created (F138 vada 11: the twin's handle). */
  clientCreatedAt?: string;
  isTest?: boolean;
  title: string;
  ageMinutes: number;
  createdAt: string;
  deepLink: string;
  owner: "partner" | "client" | "alex";
  status: string;
  attention: boolean;
  alsoCount?: number;
  supersededByRun?: boolean;
  eta?: DfyEtaDto;
  /** Where a running build stands. Needs no history, so unlike `eta` it is
   * there during cold start too. */
  position?: DfyBuildPositionDto;
  automationStats?: AutomationStatsDto;
}

export interface StaffQueueResponse {
  ok: true;
  role: StaffRole;
  thresholds: {
    clientBlockedHours: number;
    waitingOnClientHours: number;
    automationStaleMinutes: number;
  };
  items: StaffQueueItem[];
}

/**
 * O5 (audit F115, 2026-08-11): the deliverable rows were typed as
 * `Record<string, unknown>`, so `row.approved_by` compiled perfectly while the
 * server sends `approvedBy` (every staff row passes `mapKeys`). The value was
 * always `undefined`, the "Schváleno parťákem za klienta" branch was dead code
 * and every material said "Potvrzeno klientem" even where the partner had
 * clicked it themselves.
 *
 * 🔴 A `type` alias, deliberately NOT an `interface`: an alias keeps its
 * implicit index signature, so the helpers that still take
 * `Array<Record<string, unknown>>` compile unchanged, while a misspelt field
 * name on THIS type is a compile error. An interface would break the first,
 * an added `[key: string]: unknown` would break the second.
 */
export type StaffDeliverable = {
  id: string;
  kind: string;
  version: number;
  title?: string | null;
  status: string;
  published: boolean | number;
  publishedAt?: string | null;
  /** The user id that approved it; a STAFF id means F104 "approved on behalf". */
  approvedBy?: string | null;
  approvedAt?: string | null;
  contentMd?: string | null;
  urlOrR2?: string | null;
  salespageManifest?: unknown;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export interface StaffDetail {
  productionMaterials?: ProductionMaterial[];
  role: StaffRole;
  /** Facts about the signed-in staff member, not the client (F58). */
  viewer?: { telegramReady: boolean };
  callLinks: { onboarding: string; marketing: string };
  client: StaffMatrixClient["axes"] & {
    id: string;
    name: string;
    email: string;
    locale: Locale;
    axes: StaffMatrixClient["axes"];
    partner: StaffMatrixClient["partner"];
    owner: { id: string; name: string | null; email: string } | null;
  };
  appWizard: {
    submitted: boolean;
    submittedAt: string | null;
    progress: { done: number; total: number; percent: number };
  } | null;
  /** F136b: the whole app track, derived ONCE on the server. Never re-derive
   *  any of it from a raw resource on a surface (the fixr5 law). */
  appTrack?: import("../../shared/app-track-truth").AppTrackTruth | null;
  /** F112: the LATEST bot transfer job, so the beta step can say what the
   *  machine tried, how it ended and whose move it is now. */
  botTransfer?: {
    jobId: string;
    status: "pending" | "running" | "done" | "failed";
    attempts: number;
    reason: string | null;
    providerMessage: string | null;
    transcript: string | null;
    botUsername: string;
    recipientUsername: string;
    clientFault: boolean;
    createdAt: string;
    updatedAt: string;
    completedAt: string | null;
    returnedAt: string | null;
    returnReason: string | null;
  } | null;
  /** F128: the LATEST owner-role job. Same reason as the transfer above, one
   *  step later - and here the instance's own sentence is the ONLY evidence
   *  behind the verdict, so it travels with the row. */
  ownerRole?: {
    jobId: string;
    status: "pending" | "running" | "done" | "failed";
    attempts: number;
    reason: string | null;
    providerMessage: string | null;
    transcript: string | null;
    botUsername: string;
    clientFault: boolean;
    createdAt: string;
    updatedAt: string;
    completedAt: string | null;
  } | null;
  nextCall: StaffMatrixClient["nextCall"];
  sections: StaffSection[];
  steps: StaffRailStep[];
  answers: Array<Record<string, unknown>>;
  submissions: Array<Record<string, unknown>>;
  profileUpdates: Array<Record<string, unknown>>;
  profileFacts: Array<{
    id: string;
    factKey: string;
    category: string;
    text: string;
  }>;
  feedback: Array<Record<string, unknown>>;
  resources: Array<Record<string, unknown>>;
  deliverables: StaffDeliverable[];
  dfyRuns: DfyBuildRunDto[];
  /** Live training visibility (T9): latest Instance Creator run + resume state. */
  trainingRun?: TrainingRunDto | null;
  trainingRunAttempts?: number;
  trainingResumeRequestedAt?: string | null;
  calls: Array<Record<string, unknown>>;
  milestones: Array<Record<string, unknown>>;
  automationStatsByKind?: Record<string, AutomationStatsDto>;
  activeAutomationKinds?: string[];
  audit: Array<Record<string, unknown>>;
  notifications?: Array<Record<string, unknown> & {
    attempts?: Array<Record<string, unknown>>;
  }>;
}

/* F138a: `StaffDetailPaneProps` went with the tab dispatcher it typed. It had
 * no reader outside this file even before that; the live pane shape is
 * `StaffPaneProps` in console-types.ts. */

export function selectedStaffClientAfterMatrixLoad(
  selectedId: string | null,
  clients: StaffMatrixClient[],
  loading: boolean,
  hideTestTenants = true
): string | null {
  if (!selectedId || loading) return selectedId;
  return clients.some((client) =>
    client.id === selectedId && (!hideTestTenants || !client.is_test))
    ? selectedId
    : null;
}

export function nextStaffDetailRequestVersion(current: number): number {
  return current + 1;
}

export function isStaffDetailRequestCurrent(request: number, current: number): boolean {
  return request === current;
}

/**
 * The status a mutation gets when the request never reached the server
 * (audit S2-01). `fetch` does not answer a lost connection with a response,
 * it THROWS, and not one mutation in Servis was wrapped: "Poslat test" stayed
 * on "Pracuji na tom." with a permanently disabled button, "Spustit přepis
 * znovu" stayed on "Spouštím…", and saving the Stripe settings said nothing at
 * all. The only way out was a reload nobody offered.
 *
 * So the throw is caught HERE, at the one seam every mutation already passes
 * through, and turned into a response the existing `!response.ok` branch can
 * read. One fix, every panel: a control can no longer freeze because the wifi
 * dropped. 599 is outside every status the Worker returns, so a caller can
 * tell "the network failed" from "the server refused" and say so.
 */
export const STAFF_MUTATION_OFFLINE_STATUS = 599;

export function isOfflineMutation(response: { status: number }): boolean {
  return response.status === STAFF_MUTATION_OFFLINE_STATUS;
}

/**
 * One sentence for the lost connection, wherever a Servis panel reports a
 * failed mutation. The panel keeps its own wording for everything the server
 * actually answered; only the case the operator can DO something about gets
 * taken over.
 */
export function staffMutationErrorKey(response: { status: number }, panelErrorKey: string): string {
  return isOfflineMutation(response) ? "staffConsole.mutationOffline" : panelErrorKey;
}

export async function staffMutation(
  path: string,
  options: StaffMutationOptions
): Promise<Response> {
  const headers: Record<string, string> = {
    "X-Launchpad-CSRF": options.csrfToken,
    "Idempotency-Key": options.commandId ?? crypto.randomUUID()
  };
  const isForm = options.body instanceof FormData;
  if (options.body !== undefined && !isForm) {
    headers["Content-Type"] = "application/json";
  }
  try {
    return await (options.fetcher ?? fetch)(path, {
      method: options.method,
      credentials: "include",
      headers,
      body: options.body === undefined
        ? undefined
        : isForm
          ? options.body as FormData
          : JSON.stringify(options.body)
    });
  } catch {
    // A body, so a caller that reads `await response.json()` before looking at
    // the status still gets an object instead of a second throw.
    return new Response(JSON.stringify({ ok: false, error: "network_unreachable" }), {
      status: STAFF_MUTATION_OFFLINE_STATUS,
      headers: { "Content-Type": "application/json" }
    });
  }
}
